import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useBlogPosts,
  useToggleBlogPostPublished,
  useDeleteBlogPost,
  type AdminBlogPost as BlogPost,
} from "@/hooks/useBlogPosts";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";

interface BlogManagerProps {
  /** Rendered inside ContentHub — hides the standalone header. */
  embedded?: boolean;
}

export function BlogManager({ embedded = false }: BlogManagerProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: posts = [], isLoading: loading, isError } = useBlogPosts();
  const togglePublishMutation = useToggleBlogPostPublished();
  const deleteMutation = useDeleteBlogPost();

  useEffect(() => {
    if (isError) {
      toast({
        title: "Erro ao carregar posts",
        description: "Não foi possível carregar a lista de posts.",
        variant: "destructive",
      });
    }
  }, [isError, toast]);

  const togglePublish = async (post: BlogPost) => {
    const newPublished = !post.published;
    try {
      await togglePublishMutation.mutateAsync({ id: post.id, published: newPublished });
      toast({
        title: newPublished ? "Post publicado" : "Post despublicado",
        description: newPublished
          ? "O post está agora visível no blog."
          : "O post foi removido do blog público.",
      });
    } catch (error) {
      console.error("Error toggling publish:", error);
      toast({
        title: "Erro",
        description: "Não foi possível alterar o status do post.",
        variant: "destructive",
      });
    }
  };

  const deletePost = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast({
        title: "Post excluído",
        description: "O post foi removido permanentemente.",
      });
    } catch (error) {
      console.error("Error deleting post:", error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o post.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-[22px]">
      {!embedded && (
        <PageHeader
          title="Blog"
          description="Gerencie os artigos do seu blog"
          actions={
            <Button
              className="h-[38px] rounded-[10px] px-4 text-[13px] font-semibold"
              onClick={() => navigate("/admin/blog/new")}
            >
              Novo post
            </Button>
          }
        />
      )}

      <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
        {loading ? (
          <div className="space-y-3 p-5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-[74px] animate-pulse rounded-xl bg-muted" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <EmptyState
            title="Nenhum artigo criado ainda."
            className="py-12"
            action={
              <Button onClick={() => navigate("/admin/blog/new")}>
                <Plus className="mr-2 h-4 w-4" />
                Criar primeiro artigo
              </Button>
            }
          />
        ) : (
          posts.map((post) => (
            <div
              key={post.id}
              className="flex items-center gap-[18px] border-b border-border px-[22px] py-4 last:border-b-0 hover:bg-muted/40"
            >
              <div className="h-[42px] w-14 shrink-0 overflow-hidden rounded-[9px] border border-border bg-muted">
                {post.cover_image_url && (
                  <img src={post.cover_image_url} alt="" className="h-full w-full object-cover" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-semibold">{post.title}</div>
                <div className="mt-[3px] truncate text-[11.5px] text-muted-foreground">
                  {post.blog_categories?.name ?? "Sem categoria"} ·{" "}
                  {format(new Date(post.published_at ?? post.created_at), "dd/MM/yyyy", {
                    locale: ptBR,
                  })}
                </div>
              </div>
              <span
                className={`inline-flex h-[23px] items-center rounded-[7px] px-2.5 text-[11px] font-bold ${post.published ? "bg-ok-soft text-ok" : "bg-muted text-muted-foreground"}`}
              >
                {post.published ? "Publicado" : "Rascunho"}
              </span>
              <div className="flex items-center gap-1">
                {post.published && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-[31px] w-[31px]"
                    onClick={() => window.open(`/blog/${post.slug}`, "_blank")}
                    title="Ver no site"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-[31px] w-[31px]"
                  onClick={() => togglePublish(post)}
                  title={post.published ? "Despublicar" : "Publicar"}
                >
                  {post.published ? (
                    <EyeOff className="h-3.5 w-3.5" />
                  ) : (
                    <Eye className="h-3.5 w-3.5" />
                  )}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-[31px] w-[31px] text-bad hover:text-bad"
                      title="Excluir"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Excluir post?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta ação não pode ser desfeita. O post será permanentemente removido.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => deletePost(post.id)}
                        className="bg-bad text-bad-foreground hover:bg-bad/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button
                  variant="outline"
                  className="h-[31px] rounded-lg px-[13px] text-xs font-semibold"
                  onClick={() => navigate(`/admin/blog/edit/${post.id}`)}
                >
                  Editar
                </Button>
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
